import React, { useContext, useEffect, useState } from 'react';
import {
  fetchApi,
  getCurrentUserHook,
  getHeaderValue,
  getTranslatedText,
  importArticleValidator,
  preFormatForm,
  toast,
  useActivityTracker,
  useCurrentLanguage,
  validateForm
} from '@nile/components';
import {
  Badge,
  DisplayText,
  Link,
  Modal,
  Page,
  TextStyle
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropZoneMultiple } from '@nile/components/src/Modals/PromptModal';
import {
  ALLOWED_MIME_TYPES,
  supportedFormats
} from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import { CollectionSelector } from '@nile/rmm/src/modules/knowledge-base/subpages/article-page/MoveArticle';
import ImportVisibility from '@nile/rmm/src/modules/knowledge-base/components/ImportArticle/ImportVisibility';
import ActionToast from '@nile/rmm/src/modules/common/components/sub-pages/notification-toast/index.js';
import { getParentHierarchy } from '@nile/rmm/src/modules/knowledge-base/helper';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { set, isEmpty, last, cloneDeep } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';
import ImportProgress from '@nile/rmm/src/modules/knowledge-base/components/ImportArticle/ImportProgress';
import { KB_CONSTANTS } from '@nile/rmm/src/modules/knowledge-base/constants/kb-constants';

const defaultValue = {
  label: null,
  value: null,
  id: null,
  itemId: null,
  parent: null
};
function ImportArticle({ onClose: onModalClose, article }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const tabContext = useContext(TabContext);
  const user = getCurrentUserHook();
  const lang = useCurrentLanguage();
  const [parentCollection, setParentCollection] = useState([
    defaultValue,
    defaultValue
  ]);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [visibility, setVisibility] = useState({
    portalType: 'TECHNICIAN',
    userSharedType: 'User',
    groupSharedType: 'AllGroups',
    sharedType: 'ONLY_ME',
    addedUserIds: [user?.userId],
    deletedUserIds: null,
    addedGroupIds: null,
    deletedGroupIds: null,
    groups: []
  });
  const intl = useIntl();

  useEffect(() => {
    if (article?.isViews) return;
    let articleColl = cloneDeep(article?.parent);
    const isSubCollection = !isEmpty(article?.parent);
    if (
      article?.itemType === KB_CONSTANTS.KB_COLLECTION &&
      !isEmpty(articleColl)
    ) {
      articleColl.parent = {
        ...article
      };
    }
    const parentColl = getParentHierarchy(
      articleColl?.parent || isSubCollection ? article : article?.parent
    );
    if (!isEmpty(parentColl)) {
      const clonedParentColl = [...parentCollection];
      parentColl.forEach((el, idx) => {
        if (idx < clonedParentColl?.length) {
          clonedParentColl[idx] = {
            ...clonedParentColl[idx],
            ...el,
            label: el?.name,
            value: el?.itemId
          };
        }
      });
      setParentCollection(clonedParentColl);
    } else {
      const clonedParentColl = [...parentCollection];
      clonedParentColl[0] = {
        ...clonedParentColl[0],
        ...article,
        label: article?.name,
        value: article?.itemId
      };
      setParentCollection(clonedParentColl);
    }
  }, [article]);

  const handleFolderChange = (newValue, index) => {
    let updatedParentCollection;
    if (index === 0) {
      updatedParentCollection = parentCollection.map((_, i) =>
        i === index ? newValue?.[0] : defaultValue
      );
    } else {
      updatedParentCollection = set(
        parentCollection,
        `${[index]}`,
        newValue?.[0]
      );
    }
    setParentCollection(updatedParentCollection);
  };

  const createImport = async (fileClone) => {
    const filteredParentCollection = parentCollection?.filter(
      (el) => el?.itemId || el?.value
    );

    try {
      const simplifiedFiles = fileClone?.map((file, idx) => ({
        fileName: file.fileName,
        originalFileName: file.originalFileName,
        fileSize: file.fileSize
      }));
      delete visibility.sharedType;

      const rawData = {
        attachments: simplifiedFiles,
        details: {
          parent: {
            itemId: `${last(filteredParentCollection)?.itemId}`
          },
          visibility: {
            added: [visibility]
          }
        },
        module: 'KB_ARTICLE'
      };

      const requestOptions = {
        method: 'POST',
        body: JSON.stringify(rawData),
        credentials: 'include',
        headers: { ...getHeaderValue(), 'Content-Type': 'application/json' }
      };

      const response = await fetchApi(
        `${envConfig.loginRoot}/import`,
        requestOptions
      );
      const data = await response.json();
      if (!data) {
        toast('error', `app.errorCode.${response.status}`);
      } else {
        if (data?.data?.importId) {
          useActivityTracker({
            event: 'KB_ARTICLE_IMPORT',
            data: {
              fileCount: simplifiedFiles?.length
            }
          });
          ActionToast({
            renderer: ImportProgress,
            option: {
              closeButton: true,
              data: {
                ...data?.data
              },
              fileCount: simplifiedFiles?.length,
              intl,
              tabContext,
              lang
            }
          });
        }
      }
      onModalClose();
      setLoading(false);
    } catch (err) {
      onModalClose();
      setLoading(false);
      new Error(err);
    }
  };

  const loadFile = async () => {
    const formData = new FormData();
    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };
    try {
      files.forEach((file, idx) => {
        formData.append(`files`, file);
      });
      formData.append(
        'details',
        JSON.stringify({
          importModule: 'KB_ARTICLE'
        })
      );
      formData.append('module', 'IMPORT');
      const response = await fetchApi(
        `${envConfig.fileRoot}/upload`,
        requestOptions
      );
      const data = await response.json();
      if (data) {
        return data?.data;
      } else {
        toast('error', `app.errorCode.${response.status}`);
      }
    } catch (err) {
      new Error(err);
    }
  };

  const importArticles = async () => {
    setLoading(true);
    let articleError = validateForm(
      preFormatForm({
        parent: parentCollection[0],
        visibility,
        files
      }),
      importArticleValidator
    );
    if (
      visibility?.groupSharedType === 'Group' &&
      isEmpty(visibility?.groups)
    ) {
      articleError = { visibility: ['Visibility is required'] };
    }
    if (articleError) {
      toast('error', 'rmm.schedule.fill.required.fields');
      setError(articleError);
      setLoading(false);
      return;
    }
    const fileData = await loadFile();
    await createImport(fileData);
  };

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <Modal
      open={open}
      large
      onClose={() => {
        setOpen(false);
      }}
      showCloseButton={false}
    >
      <Page
        title={getTranslatedText(intl, 'common.app.kb.article.import')}
        contentHeight="100px"
        primaryAction={{
          content: (
            <span className="fx g8 actr">
              <SVGIcon name="Save" />
              <TextStyle>
                <FormattedMessage id="common.save" />
              </TextStyle>
            </span>
          ),
          onClick: importArticles,
          loading
        }}
        secondaryActions={[
          {
            content: getTranslatedText(intl, 'common.cancel'),
            onClick: () => {
              onModalClose();
            }
          }
        ]}
      >
        <div className="flex fcol g20 article-import">
          <div className="flex fcol g10">
            <DisplayText element="h3" weight="SemiBold">
              <FormattedMessage id="common.app.kb.article.import.collection.title" />
            </DisplayText>
            <div className="flex fcol g10 mt10 w50">
              {parentCollection?.map((el, index) => (
                <CollectionSelector
                  intl={intl}
                  updateParent={(val) => handleFolderChange(val, index)}
                  parentFolder={
                    index === 0 ? null : parentCollection[index - 1]
                  }
                  currentFolder={parentCollection[index]}
                  isCollection={index === 0}
                  setCreatingCollection={setCreatingCollection}
                  creatingCollection={creatingCollection}
                />
              ))}
            </div>
            {error?.['parent.value'] && (
              <TextStyle variation="negative">
                {error?.['parent.value']}
              </TextStyle>
            )}
          </div>
          <div className="flex fcol g10">
            <DisplayText element="h3" weight="SemiBold">
              <FormattedMessage id="common.app.kb.article.import.visibility.title" />
            </DisplayText>
            <ImportVisibility
              visibility={visibility}
              setVisibility={setVisibility}
            />
          </div>
          <div className="flex fcol g10">
            <DisplayText element="h3" weight="SemiBold">
              <FormattedMessage id="common.app.kb.article.import" />
            </DisplayText>
            {error?.['files'] && (
              <TextStyle variation="negative">{error?.['files']}</TextStyle>
            )}
            <DropZoneMultiple
              files={files}
              setFiles={setFiles}
              allowMultiple
              limitFileSize={100}
              bulkFileValidator
              intl={intl}
              bgNone
              acceptType={ALLOWED_MIME_TYPES}
              maxFiles={20}
              description={
                <div className="flex-center fcol mbt10 mb-30 g8">
                  <DisplayText element="h3" weight="SemiBold">
                    <FormattedMessage
                      id="common.file.upload.modal.text"
                      values={{
                        a: (chunks) => (
                          <span className="d-in">
                            <Link>{chunks}</Link>
                          </span>
                        )
                      }}
                    />
                  </DisplayText>
                  <div className="flex actr g8">
                    <TextStyle weight="SemiBold">
                      <FormattedMessage id="common.file.upload.modal.supported.format" />
                    </TextStyle>
                    {supportedFormats?.map((el) => (
                      <Badge
                        status={el?.variant}
                      >{`.${el?.key?.toUpperCase()}`}</Badge>
                    ))}
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </Page>
    </Modal>
  );
}

export default ImportArticle;
